import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Workflow as WorkflowIcon, Play, Loader2, Beaker, X, Mail, Phone, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DBWorkflow {
  id: string;
  name: string;
  description: string | null;
  trigger_type: string | null;
  is_active: boolean;
  company_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function formatTriggerType(triggerType: string | null | undefined): string {
  if (!triggerType) return '—';
  return triggerType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

export default function WorkflowsPage() {
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState<DBWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningWorkflowId, setRunningWorkflowId] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<{ workflowId: string; message: string; success: boolean } | null>(null);

  // Test modal state
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testingWorkflow, setTestingWorkflow] = useState<DBWorkflow | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResults, setTestResults] = useState<Array<{ step: string; type: string; success: boolean; error?: string }> | null>(null);

  // Send window info
  const [sendWindowInfo, setSendWindowInfo] = useState<{ isOpen: boolean; description: string; nextWindowStart: string } | null>(null);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('marketing_workflows')
        .select('id,name,description,trigger_type,is_active,company_id,created_at,updated_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWorkflows((data || []) as DBWorkflow[]);
    } catch (err) {
      console.error('Error loading workflows:', err);
      setError('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const runWorkflow = async (workflowId: string) => {
    try {
      setRunningWorkflowId(workflowId);
      setRunResult(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`/api/admin/workflows/${workflowId}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to run workflow');
      }

      setRunResult({
        workflowId,
        message: `Successfully enrolled ${result.data?.customersEnrolled || 0} customers`,
        success: true,
      });
    } catch (err) {
      console.error('Error running workflow:', err);
      setRunResult({
        workflowId,
        message: err instanceof Error ? err.message : 'Failed to run workflow',
        success: false,
      });
    } finally {
      setRunningWorkflowId(null);
    }
  };

  const canRunWorkflow = (wf: DBWorkflow) => {
    return wf.is_active && (wf.trigger_type === 'bulk_segment' || wf.trigger_type === 'manual');
  };

  const openTestModal = (wf: DBWorkflow) => {
    setTestingWorkflow(wf);
    setTestEmail('');
    setTestPhone('');
    setTestResults(null);
    setTestModalOpen(true);
  };

  const closeTestModal = () => {
    setTestModalOpen(false);
    setTestingWorkflow(null);
    setTestResults(null);
  };

  const runTest = async () => {
    if (!testingWorkflow || !testEmail) return;

    try {
      setTestLoading(true);
      setTestResults(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`/api/admin/workflows/${testingWorkflow.id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testEmail: testEmail.trim(),
          testPhone: testPhone.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to run test');
      }

      setTestResults(result.data?.results || []);
    } catch (err) {
      console.error('Error running test:', err);
      setTestResults([{ step: 'Error', type: 'system', success: false, error: err instanceof Error ? err.message : 'Unknown error' }]);
    } finally {
      setTestLoading(false);
    }
  };

  const loadSendWindowInfo = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch('/api/admin/workflows/send-window/info', {
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });

      if (response.ok) {
        const result = await response.json();
        setSendWindowInfo(result.data);
      }
    } catch (err) {
      console.error('Error loading send window info:', err);
    }
  };

  useEffect(() => {
    loadWorkflows();
    loadSendWindowInfo();
  }, []);

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Workflows</h1>
            <p className="text-gray-600 mt-2">
              Create and manage multi-step automations (email, SMS, direct mail).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadWorkflows}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
              disabled={loading}
            >
              <RefreshCw size={18} />
              Refresh
            </button>
            <button
              onClick={() => navigate('/admin/workflows/new')}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              New Workflow
            </button>
          </div>
        </div>

        {/* Send Window Info */}
        {sendWindowInfo && (
          <div className={`mt-4 flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
            sendWindowInfo.isOpen
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-amber-50 text-amber-800 border border-amber-200'
          }`}>
            <Clock size={16} />
            <span className="font-medium">Send Window:</span>
            <span>{sendWindowInfo.description}</span>
            <span className="text-gray-500">•</span>
            <span>
              {sendWindowInfo.isOpen
                ? 'Currently open'
                : `Opens ${new Date(sendWindowInfo.nextWindowStart).toLocaleString()}`
              }
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="card border border-red-200 bg-red-50 mb-6">
          <div className="text-red-800 font-medium">{error}</div>
          <div className="text-red-700 text-sm mt-1">Try refreshing, or check your Supabase connection/RLS policies.</div>
        </div>
      )}

      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading workflows...</p>
            </div>
          </div>
        </div>
      ) : workflows.length === 0 ? (
        <div className="card text-center py-12">
          <WorkflowIcon className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No workflows yet</h3>
          <p className="text-gray-600 mb-4">
            Create your first workflow to start automating winbacks, reminders, and follow-ups.
          </p>
          <button
            onClick={() => navigate('/admin/workflows/new')}
            className="btn-primary"
          >
            <Plus size={20} className="inline mr-2" />
            Create Your First Workflow
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {workflows.map((wf) => (
            <div key={wf.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{wf.name}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        wf.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {wf.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {wf.description && (
                    <p className="text-gray-600 mb-3">{wf.description}</p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>
                      <span className="font-medium text-gray-600">Trigger:</span>{' '}
                      {formatTriggerType(wf.trigger_type)}
                    </span>
                    <span>
                      <span className="font-medium text-gray-600">Created:</span>{' '}
                      {formatDate(wf.created_at)}
                    </span>
                    {wf.updated_at && (
                      <span>
                        <span className="font-medium text-gray-600">Updated:</span>{' '}
                        {formatDate(wf.updated_at)}
                      </span>
                    )}
                  </div>

                  {/* Run result message */}
                  {runResult?.workflowId === wf.id && (
                    <div className={`mt-2 text-sm px-3 py-1 rounded ${
                      runResult.success
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {runResult.message}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => openTestModal(wf)}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
                    title="Send test messages to yourself"
                  >
                    <Beaker size={18} />
                    Test
                  </button>
                  {canRunWorkflow(wf) && (
                    <button
                      onClick={() => runWorkflow(wf.id)}
                      disabled={runningWorkflowId === wf.id}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                      title="Run workflow for all matching customers"
                    >
                      {runningWorkflowId === wf.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Play size={18} />
                      )}
                      Run
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/admin/workflows/${wf.id}`)}
                    className="btn-primary"
                  >
                    Open
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Test Modal */}
      {testModalOpen && testingWorkflow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Test Workflow</h2>
                <p className="text-sm text-gray-600">{testingWorkflow.name}</p>
              </div>
              <button
                onClick={closeTestModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Send all workflow steps (emails & SMS) to your test recipients.
                Time restrictions are bypassed for testing.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail size={16} className="inline mr-1" />
                  Test Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone size={16} className="inline mr-1" />
                  Test Phone Number (optional)
                </label>
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Required if workflow includes SMS steps</p>
              </div>

              <button
                onClick={runTest}
                disabled={testLoading || !testEmail}
                className="w-full px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {testLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending Test...
                  </>
                ) : (
                  <>
                    <Beaker size={18} />
                    Send Test Messages
                  </>
                )}
              </button>

              {/* Test Results */}
              {testResults && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b">
                    <span className="font-medium text-sm text-gray-700">Results</span>
                  </div>
                  <div className="divide-y">
                    {testResults.map((result, idx) => (
                      <div key={idx} className="px-3 py-2 flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${result.success ? 'bg-green-500' : 'bg-red-500'}`} />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{result.step}</div>
                          <div className="text-xs text-gray-500">
                            {result.type}
                            {result.error && <span className="text-amber-600 ml-2">• {result.error}</span>}
                          </div>
                        </div>
                        <span className={`text-xs font-medium ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                          {result.success ? 'Sent' : 'Failed'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
